import React, { useEffect } from 'react';
import { Check, AlertTriangle, AlertCircle, X, Info } from 'lucide-react';

/**
 * Toast — v2 ephemeral notification.
 *
 * The presentation primitive for in-app notifications. Pairs with a
 * stack manager (added in a later PR) that handles positioning,
 * z-stacking, and queueing of multiple concurrent toasts.
 *
 * Variants by tone (success/info/warning/danger). Auto-dismisses after
 * `duration` ms; pass duration={0} to disable auto-dismiss (useful when
 * the toast carries a required action).
 *
 * Accessibility:
 *   - role="alert" + aria-live="polite" so screen readers announce on
 *     mount without interrupting current focus.
 *   - Icon is decorative (aria-hidden); the text content carries meaning.
 *
 * @param {Object} props
 * @param {React.ReactNode}                              props.title
 * @param {React.ReactNode}                              [props.description]
 * @param {'success'|'info'|'warning'|'danger'}          [props.tone='info']
 * @param {number}                                       [props.duration=4000]
 *   Auto-dismiss timeout in ms. 0 = no auto-dismiss.
 * @param {() => void}                                   [props.onDismiss]
 *   Required for the dismiss button to render.
 * @param {React.ReactNode}                              [props.action]
 *   Optional action slot rendered below the description (e.g. an Undo).
 */
export function Toast({
  title,
  description,
  tone = 'info',
  duration = 4000,
  onDismiss,
  action,
}) {
  useEffect(() => {
    if (!onDismiss || duration <= 0) return undefined;
    const id = window.setTimeout(onDismiss, duration);
    return () => window.clearTimeout(id);
  }, [duration, onDismiss]);

  const ICON = {
    success: <Check size={14} />,
    info:    <Info size={14} />,
    warning: <AlertTriangle size={14} />,
    danger:  <AlertCircle size={14} />,
  }[tone] || <Info size={14} />;

  const BORDER_LEFT = {
    success: 'border-l-v2-success',
    info:    'border-l-v2-action',
    warning: 'border-l-v2-warning',
    danger:  'border-l-v2-danger',
  }[tone] || 'border-l-v2-action';

  const ICON_BG = {
    success: 'bg-v2-success-soft text-v2-success',
    info:    'bg-v2-action-soft  text-v2-action',
    warning: 'bg-v2-warning-soft text-v2-warning',
    danger:  'bg-v2-danger-soft  text-v2-danger',
  }[tone] || 'bg-v2-action-soft text-v2-action';

  return (
    <div
      role="alert"
      aria-live="polite"
      className={
        'bg-v2-surface border border-v2-border-subtle border-l-4 ' + BORDER_LEFT + ' ' +
        'rounded-v2-md shadow-v2-md p-3.5 max-w-[380px] ' +
        'flex items-start gap-3 slide-up'
      }
    >
      <div
        aria-hidden="true"
        className={
          'w-7 h-7 rounded-v2-sm flex items-center justify-center flex-shrink-0 ' + ICON_BG
        }
      >
        {ICON}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold text-v2-ink leading-tight">{title}</div>
        {description && (
          <div className="text-[12px] text-v2-ink-secondary leading-relaxed mt-0.5">
            {description}
          </div>
        )}
        {action && <div className="mt-2">{action}</div>}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss notification"
          className={
            'v2-focus-ring w-6 h-6 rounded flex items-center justify-center ' +
            'text-v2-ink-muted hover:bg-v2-muted hover:text-v2-ink ' +
            'transition-colors duration-v2 ease-v2 flex-shrink-0'
          }
        >
          <X size={12} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

export default Toast;
